import type {
  BdAdministrativeDistrict,
  BdPostalCode,
} from '@bindoc-projects/geographic-domain-lib';
import type {
  VirtualClinicAnalysisPatientDistributionTableDataChildItem,
  VirtualClinicAnalysisPatientDistributionTableDataItem,
} from './table-data';
import type { VirtualClinicAnalysisPatientDistributionAdministrativeDistrict } from './patient-distribution-administrative-district';
import type { VirtualClinicAnalysisPatientDistributionPostalCode } from './patient-distribution-postal-code';
import type { VirtualClinicAnalysisOperatingNumberResponsePart } from '../resolving/response';
import type {
  VirtualClinicAnalysisOperatingNumberLeafItem,
  VirtualClinicAnalysisOperatingNumberNodeItem,
} from '../resolving/operating-number-item';

export class VirtualClinicAnalysisPatientDistribution {
  public static of(
    data: VirtualClinicAnalysisOperatingNumberResponsePart,
    administrativeDistrictMapping: {
      [districtId: string]: BdAdministrativeDistrict;
    },
    postalCodesMapping: {
      [postalCode: string]: BdPostalCode;
    },
  ): VirtualClinicAnalysisPatientDistribution {
    const patientCount: {
      [
        adId: number
      ]: VirtualClinicAnalysisPatientDistributionAdministrativeDistrict;
    } = {};

    (
      data.groupedValues as VirtualClinicAnalysisOperatingNumberNodeItem[]
    ).forEach((districtValue: VirtualClinicAnalysisOperatingNumberNodeItem) => {
      const postalCodes: {
        [
          postalCode: string
        ]: VirtualClinicAnalysisPatientDistributionPostalCode;
      } = {};
      const percentage = districtValue.aggregated / data.aggregatedValue;
      const districtName = administrativeDistrictMapping[
        districtValue.label.key
      ]
        ? administrativeDistrictMapping[districtValue.label.key].displayName()
        : 'n/a';

      (
        districtValue.groupedValues as VirtualClinicAnalysisOperatingNumberLeafItem[]
      ).forEach(
        (postalCodeValue: VirtualClinicAnalysisOperatingNumberLeafItem) => {
          const name = postalCodesMapping[postalCodeValue.label.key]
            ? postalCodesMapping[postalCodeValue.label.key].getDisplayName()
            : postalCodeValue.label.shortDisplayName;

          postalCodes[postalCodeValue.label.key] = {
            count: postalCodeValue.value,
            name,
          };
        },
      );

      patientCount[districtValue.label.key] = {
        count: districtValue.aggregated,
        name: districtName,
        percentage,
        postalCodes,
      };
    });

    return new VirtualClinicAnalysisPatientDistribution(patientCount);
  }

  public data: VirtualClinicAnalysisPatientDistributionTableDataItem[];
  public postalCodeLayerData: { [postalCode: string]: number };

  constructor(
    public patientCount: {
      [
        adId: number
      ]: VirtualClinicAnalysisPatientDistributionAdministrativeDistrict;
    },
  ) {
    this.init();

    this.postalCodeLayerData = this.data.reduce(
      (
        previousValue: any,
        currentValue: VirtualClinicAnalysisPatientDistributionTableDataItem,
      ) => {
        currentValue.children.forEach(
          (child) => (previousValue[child.postalCode] = child.total),
        );

        return previousValue;
      },
      {},
    );
  }

  public init() {
    const adIds = Object.keys(this.patientCount);

    this.data = adIds
      .map((adId) => {
        const districtData: VirtualClinicAnalysisPatientDistributionAdministrativeDistrict =
          this.patientCount[adId];
        const tableDataItem: VirtualClinicAnalysisPatientDistributionTableDataItem =
          {
            adId,
            name: districtData.name,
          };

        const postalCodes = Object.keys(districtData.postalCodes);
        tableDataItem.children = postalCodes
          .map((postalCode) => {
            const postalCodeData: VirtualClinicAnalysisPatientDistributionPostalCode =
              districtData.postalCodes[postalCode];

            return {
              postalCode,
              name: postalCodeData.name,
              total: postalCodeData.count,
            };
          })
          .sort((a, b) => {
            let result = 0;

            if (a.total > b.total) {
              result = -1;
            } else if (a.total < b.total) {
              result = 1;
            }

            return result;
          });

        tableDataItem.total = tableDataItem.children.reduce(
          (
            previousValue: number,
            currentValue: VirtualClinicAnalysisPatientDistributionTableDataChildItem,
          ) => {
            return previousValue + currentValue.total;
          },
          0,
        );

        return tableDataItem;
      })
      .sort((a, b) => {
        let result = 0;

        if (a.total > b.total) {
          result = -1;
        } else if (a.total < b.total) {
          result = 1;
        }

        return result;
      });
  }
}
